import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Pencil, Power, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Generic config-driven CRUD table for simple facility-scoped entities.
export function CrudTable({ endpoint, facilityId, columns, fields, canEdit = true, entityName = "record", testId }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const empty = Object.fromEntries(fields.map((f) => [f.key, f.type === "number" ? 0 : ""]));
  const load = () => { if (!facilityId) return; setRows(null); api.get(`/${endpoint}?facility_id=${facilityId}`).then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(load, [facilityId, endpoint]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...empty, ...r }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, facility_id: facilityId };
      if (editing) await api.put(`/${endpoint}/${editing.id}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success(editing ? `${entityName} updated` : `${entityName} created`);
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const toggle = async (r) => { try { await api.patch(`/${endpoint}/${r.id}/status`); toast.success("Status updated"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const remove = async () => { try { await api.delete(`/${endpoint}/${confirmDel.id}`); toast.success(`${entityName} deleted`); setConfirmDel(null); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setConfirmDel(null); } };

  return (
    <div data-testid={testId}>
      {canEdit && <div className="flex justify-end mb-4"><Button onClick={openCreate} disabled={!facilityId} className="bg-blue-700 hover:bg-blue-800" data-testid={`create-${endpoint}-button`}><Plus className="h-4 w-4 mr-1.5" /> New {entityName}</Button></div>}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">{columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}<TableHead>Status</TableHead>{canEdit && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`${endpoint}-row-${r.id}`}>
                  {columns.map((c) => <TableCell key={c.key} className={c.mono ? "font-mono text-xs text-slate-500" : "text-sm text-slate-700"}>{c.render ? c.render(r) : (r[c.key] ?? "—")}</TableCell>)}
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  {canEdit && <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`${endpoint}-actions-${r.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(r)} data-testid={`${endpoint}-edit-${r.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggle(r)}><Power className="h-4 w-4 mr-2" /> {r.status === "active" ? "Deactivate" : "Activate"}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setConfirmDel(r)} className="text-red-600 focus:text-red-700" data-testid={`${endpoint}-delete-${r.id}`}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>}
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={columns.length + 2} className="text-center text-slate-400 py-10">No {entityName.toLowerCase()}s yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${endpoint}-dialog`}>
          <DialogHeader><DialogTitle>{editing ? `Edit ${entityName}` : `New ${entityName}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {fields.map((f) => (
              <div key={f.key} className={`space-y-1.5 ${f.full ? "col-span-2" : ""}`}>
                <Label>{f.label}</Label>
                <Input type={f.type === "number" ? "number" : "text"} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? parseFloat(e.target.value || 0) : e.target.value })} data-testid={`${endpoint}-${f.key}-input`} />
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving || !form[fields[0].key]} className="bg-blue-700 hover:bg-blue-800" data-testid={`${endpoint}-save-button`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={() => setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete {entityName}?</AlertDialogTitle><AlertDialogDescription>This will permanently remove “{confirmDel?.name}”. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-button">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
